import React, { AnchorHTMLAttributes, ButtonHTMLAttributes, FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import CircleLoader from '../circle-loader';

enum ButtonStyleVariant {
  BLUE_ROUNDED = 'bg-brand-700 px-8 py-[14px] text-white font-inter font-[500] text-sm  disabled:ring-1 disabled:ring-gray-200 disabled:bg-gray-100 disabled:text-gray-300 hover:bg-[#146EF5]',
  DEFAULT_ROUNDED = 'bg-transparent px-4 py-[14px] text-gray-900 font-inter font-[500] text-sm  disabled:ring-1 disabled:ring-gray-200 disabled:bg-gray-100 disabled:text-gray-300 hover:bg-[white]',
  NONE = '',
}

export enum ButtonType {
  BUTTON = 'button',
  LINK = 'link',
}

type ButtonBaseProps = {
  children?: ReactNode;
  className?: string;
  styleType?: keyof typeof ButtonStyleVariant;
};

type ButtonElementProps = {
  as?: ButtonType.BUTTON;
  isLoading?: boolean;
} & ButtonBaseProps &
  ButtonHTMLAttributes<HTMLButtonElement>;

type LinkElementProps = {
  as: ButtonType.LINK;
  href: string;
} & AnchorHTMLAttributes<HTMLAnchorElement> &
  ButtonBaseProps;

export type ButtonsProps = ButtonElementProps | LinkElementProps;

const Button: FC<ButtonsProps> = (props) => {
  const { as = ButtonType.BUTTON, styleType, className, children, ...rest } = props;
  const mergedClassName = twMerge(
    'flex w-full cursor-pointer items-center justify-center text-nowrap rounded transition-all duration-300 hover:bg-opacity-85 disabled:cursor-default disabled:bg-gray-100 disabled:text-gray-300 disabled:ring-1 disabled:ring-gray-200',
    styleType && ButtonStyleVariant[styleType],
    className,
  );

  if (as === ButtonType.LINK) {
    const { href, ...anchorProps } = rest as Omit<
      LinkElementProps,
      'as' | 'children' | 'className' | 'styleType'
    >;

    return (
      <a
        className={mergedClassName}
        href={href}
        rel="noreferrer noopener nofollow"
        target="_blank"
        {...anchorProps}
      >
        {children}
      </a>
    );
  }

  const { isLoading, disabled, ...buttonProps } = rest as Omit<
    ButtonElementProps,
    'as' | 'children' | 'className' | 'styleType'
  >;
  return (
    <button className={mergedClassName} disabled={disabled || isLoading} {...buttonProps}>
      {isLoading ? <CircleLoader /> : children}
    </button>
  );
};

export default Button;
